// shell.ts — THE one console shell (R63): every Bridge page mounts this
// header before its own content, so navigating home ⇄ #/users ⇄ #/audit
// keeps the same brand, the same nav, the same appearance control and the
// same account circle (R60/R72). Until this seam there were TWO shells —
// mountHome's innerHTML header and adminChrome's — sharing only a class
// name: the admin pages had NO theme toggle and NO avatar, so the app
// stopped looking like one application at the first navigation.
//
// WHAT LIVES HERE (and only here): the brand — "The Bridge" (R65) — the page
// nav, the appearance control and the account menu. Page STATE lives below,
// in the .hc-pagebar row each page fills (R64): home puts "Releases", the
// slot count (#hcSlots, R66) and the drift pill (#hcDrift, R67) there; the
// admin pages their title and row count (R68). #hcColsBtn/#hcCreate ride the
// pagebar UNCHANGED in this seam — at no deployed moment does production
// lack a Columns or Create button — and grid-toolbar (wave 3) restyles them
// and finalises their place beside the grid.
//
// The shell also owns body.hc-fixed (R61): every page is viewport-fixed now,
// so the admin grids stop page-scrolling against the old calc(100vh - 110px)
// fallback exactly like the release grid.
//
// R72 HELM-PARITY DEFERRAL LEDGER — the residue that keeps "one application"
// from closing silently. The Bridge's three pages are unified HERE; the Helm
// half is explicitly before the owner (plan ruling Q11), item by item:
//   1. UiTokens widening in helm-bridge/src/client/themes.ts + theming the
//      18 --hc-p-* pill colours (deferred by design-system-foundation — a
//      five-file, 20-theme lockstep). Consequence until it lands: status
//      pills keep their generic dark/light colours under exotic themes.
//   2. The Helm's own theme picker (Settings modal, native selects) does NOT
//      get the custom themed dropdown / hover preview the Bridge gets from
//      appearance-dialog (ruling Q8 default: Bridge only).
//   3. Deploy-lane skew: a running VM's Helm updates only via the explicit
//      operator lane, so Bridge chrome (console deploy, live on merge) and
//      Helm chrome (framework bundle, fetched at VM boot) can be out of step
//      until the fleet refreshes; grid-toolbar documents the tooltip/glyph
//      half of this skew.
//   4. (adjacent residue, same ruling table) R2 stays PARTIAL: the Helm's own
//      confirmation dialogs are INVENTORIED by dialog-system, not migrated,
//      pending ruling Q12 — named here because "one application" is the
//      rationale both deferrals answer to.
// None of the four is implemented here — they are LEDGER entries, recorded
// so R72 closes as "Bridge unified + named residue", never as a silent
// three-page refactor.

import { icon } from './generated/icons.js';
import { iconBtn } from './buttons.js';
import { GROUND_HORIZON_SEAT } from './generated/style-axis.js';
import { mountBrandMarks } from './generated/brand.js';
import { mountAccountMenu, type AccountUser } from './account-menu.js';
import { mountHelpMenu } from './help-menu.js';

/** The signed-in user, as /api/me serves it. Lived in home.ts while the home
 * screen was the only page with an account circle; the shell owns the fetch
 * now (home.ts re-exports these three names, so its headless pins hold). */
export interface Me {
  id: string;
  role: 'admin' | 'release_owner' | 'member' | 'viewer';
  /** Identity for the account circle. /api/me serves all three. */
  email?: string;
  name?: string | null;
  /** Profile picture (migration 029). Null is a NORMAL state — an IdP that
   * omits the claim, or a user who has not logged in since it shipped — and
   * the circle renders initials for it. */
  pictureUrl?: string | null;
  /** Server-computed capabilities (/api/me). The client hides what the
   * server would refuse; it never re-derives policy from the role string.
   * Optional so an older cached bundle degrades to the role check. */
  /** The configured apex release links hang under (`https://{slug}.{apex}/`),
   * the server's domains[0]. Null when the console has no domain seat. The
   * client never derives it from location.hostname (see consoleApex). */
  apex?: string | null;
  caps?: {
    manageUsers: boolean; createRelease: boolean; sendChat: boolean; accessReleases: boolean;
    /** Configuration templates (owner item 65). Optional so a cached bundle
     * against an older server degrades to the role check. */
    manageConfigTemplates?: boolean;
  };
}

/** Admin-only chrome: the two admin nav entries (and home's create button).
 * Exported so the headless suite can pin it without a DOM. */
export function canManage(me: Me | null): boolean {
  if (!me) return false;
  return me.caps ? me.caps.manageUsers : me.role === 'admin';
}

export function canCreate(me: Me | null): boolean {
  if (!me) return false;
  return me.caps ? me.caps.createRelease : me.role === 'admin';
}

/** Configuration-templates chrome (owner item 65): the Templates nav entry
 * and every mutating control on those screens. Server predicate:
 * canManageConfigTemplates = the create-release bar. */
export function canManageTemplates(me: Me | null): boolean {
  if (!me) return false;
  if (me.caps) return me.caps.manageConfigTemplates ?? me.caps.createRelease;
  return me.role === 'admin' || me.role === 'release_owner';
}

export type ShellNav = 'releases' | 'templates' | 'users' | 'audit' | 'brain' | 'tokens' | 'api';

export interface Shell {
  /** The below-header page-chrome row (R64). The page fills it with ITS
   * state — title, counts, alerts — and grid-toolbar later lands its
   * buttons beside that content. Empty until the page writes it. */
  pagebar: HTMLElement;
  /** The page's content host (<main class="hc-main">). */
  main: HTMLElement;
  /** The one modal overlay host (#hcModal), shared shape on every page. */
  modal: HTMLElement;
  /** The shell's own /api/me read — ONE fetch per mount, shared with the
   * page so home does not fetch what the account circle already knows.
   * status 401 redirects to /auth/login before this resolves. */
  whoami: Promise<{ me: Me | null; status: number }>;
  /** Tear the shell down (theme control, account menu, body.hc-fixed).
   * The router MUST call this on every route change — the admin pages
   * returned void before this seam, and the moment the shell mounted
   * document-level controls on them, an unhonoured teardown became one
   * leaked listener per navigation. */
  teardown(): void;
}

/** The shell's own fetch. Deliberately local (the console client's screens
 * each carry theirs): importing admin-ui's would drag the AG Grid module
 * graph under a module whose job is a header. */
async function jsonFetch<T>(url: string): Promise<{ status: number; data: T | null }> {
  try {
    const res = await fetch(url);
    let data: T | null = null;
    try { data = (await res.json()) as T; } catch { /* tolerate non-JSON */ }
    return { status: res.status, data };
  } catch {
    return { status: 0, data: null };
  }
}

/**
 * Mount the one shell into `root` and return the hosts the page fills.
 *
 * The top strip is IDENTICAL on all three pages by construction — same
 * template, same mounts — which is what R60's "navigation looks seamless"
 * cashes out to. Only the nav's active marker differs. The strip itself is
 * deliberately NOT animated: it is the part of the page that does not
 * change across a navigation, and a header that rises in on every route
 * would read as the app reloading — the .hc-pagebar below it carries the
 * entrance instead (R58).
 */
export function mountShell(root: HTMLElement, active: ShellNav): Shell {
  const link = (nav: ShellNav, href: string, id: string, label: string, hidden: boolean): string =>
    `<a href="${href}" class="hc-navlink${active === nav ? ' hc-navlink-on' : ''}" id="${id}"` +
    `${active === nav ? ' aria-current="page"' : ''}${hidden ? ' hidden' : ''}>${label}</a>`;

  // The phone bottom-tab-bar mirror of each header nav destination (owner
  // ruling 2026-09-06: unified thumb-reachable bottom bar across the Bridge,
  // the Helm and the KB/API portals; supersedes the icon-mini-rail interim).
  // Icon + label, its own id so whoami reveals the admin tabs in lockstep with
  // the header links. These are ANCHORS, not <button>s — plain navigation, so
  // they carry no ARIA tab role (there is no tablist to establish) and
  // buttons-check does not see them. Desktop hides the whole bar (styles.css
  // @media), so the top header nav is byte-identical above 640px.
  const tab = (nav: ShellNav, href: string, id: string, ic: string, label: string, hidden: boolean): string =>
    `<a href="${href}" class="hc-tab${active === nav ? ' hc-tab-on' : ''}" id="${id}"` +
    `${active === nav ? ' aria-current="page"' : ''}${hidden ? ' hidden' : ''}>` +
    `${icon(ic)}<span>${label}</span></a>`;

  root.innerHTML = `
    <header class="hc-header">
      <a class="hc-brand" href="#/" data-tip="The Bridge"><span class="hc-brand-mark" data-brand-mark>${icon('helm')}</span><span class="hc-brand-word" data-brand-wordmark="bridge">The Bridge</span></a>
      <nav class="hc-nav" aria-label="Bridge pages">
        ${link('releases', '#/', 'hcNavReleases', 'Releases', false)}
        ${link('templates', '#/config-templates', 'hcNavTemplates', 'Templates', true)}
        ${link('users', '#/users', 'hcNavUsers', 'Users', true)}
        ${link('audit', '#/audit', 'hcNavAudit', 'Audit', true)}
        ${link('brain', '#/brain', 'hcNavBrain', 'Brain', true)}
      </nav>
      <div class="hc-spring"></div>
      ${iconBtn({
        // The phone "⋯" sheet opener rides the primitive too (S×T W2): it
        // wore the same `.hc-iconbtn` class as the help control and so drew
        // the browser's default chrome on every phone. `hc-morebtn` stays as
        // the LAYOUT hook (display: none above 640px) — layout only.
        glyph: 'ellipsis', label: 'More', id: 'hcMore', cls: 'hc-morebtn',
        attrs: { 'aria-haspopup': 'menu', 'aria-expanded': 'false' },
      })}
      <div class="hc-headtools" id="hcHeadTools">
        <div class="hc-help-host" id="hcHelp"></div>
        <div class="hc-account" id="hcAccount"></div>
      </div>
    </header>
    <div class="hc-pagebar" id="hcPagebar"></div>
    <main class="hc-main"></main>
    <nav class="hc-tabbar" id="hcTabbar" aria-label="Bridge navigation">
      ${tab('releases', '#/', 'hcTabReleases', 'house', 'Releases', false)}
      ${tab('templates', '#/config-templates', 'hcTabTemplates', 'layers', 'Templates', true)}
      ${tab('users', '#/users', 'hcTabUsers', 'crew', 'Users', true)}
      ${tab('audit', '#/audit', 'hcTabAudit', 'history', 'Audit', true)}
      ${tab('brain', '#/brain', 'hcTabBrain', 'layers', 'Brain', true)}
    </nav>
    <div id="hcModal" class="hc-modal" hidden></div>`;
  // BRAND HOOKS (owner ruling 2026-09-08): under a non-identity style the
  // console's copy of brand.ts inlines the style's standalone logo and its
  // "The Bridge" wordmark (outlines, no font dependency) into the two spans
  // above; under Blueprint they keep exactly the sprite glyph and the text.
  // The hooks exist only now, so converge them here; every later style paint
  // rides style-substrate.ts groundStyle() -> refreshBrand().
  void mountBrandMarks(root);

  const teardowns: (() => void)[] = [];
  let torn = false;
  /** Register a teardown, or run it at once if the shell is already down —
   * the account menu mounts after an await, and a fast navigation can tear
   * the shell down before /api/me answers. */
  const push = (fn: () => void): void => { torn ? fn() : teardowns.push(fn); };

  // R61: the shell owns the fixed viewport on EVERY page — all scrolling
  // happens inside the grid (the WaaG treatment), Users and Audit included.
  document.body.classList.add('hc-fixed');
  push(() => document.body.classList.remove('hc-fixed'));

  // The appearance control lives INSIDE the avatar menu now (owner ruling
  // 2026-09-08; mountAccountMenu below) — the gear and the sun that used to
  // sit in #hcAppearance are retired. R60's guarantee holds the same way: the
  // account menu mounts on every page.

  // THE GROUND'S HORIZON SEAT (owner ruling 2026-09-08: "the background of the
  // bridge-console should be visible in the space behind the jump back in
  // section"). The shell is the one place that knows where its chrome ends
  // and the open ground begins — the pagebar's bottom edge — so it writes that
  // edge, in px, into `--bgl-horizon` on <html>. A ground with a horizon
  // (Synthwave's plane resolves `--sw-horizon` through this seat, default 38%)
  // then starts UNDER the chrome instead of a third of the way down the
  // viewport, which on this surface was below the "Jump back in" band and
  // under the opaque releases grid: measured 2026-09-08, the band spans
  // ~12-31% of a 950px viewport and the canvas painted 0 pixels in it. The
  // seat follows the pagebar's real height (it wraps on narrow viewports)
  // through a ResizeObserver, and is removed with the shell so a page that
  // mounts no shell inherits nothing. Grounds without a horizon ignore it.
  push(seatGroundHorizon(root.querySelector('#hcPagebar') as HTMLElement | null));


  // The help SUBMENU (owner IA ruling): Knowledgebase + API documentation,
  // each a NEW window. Internals live in help-menu.ts (stub-DOM tested);
  // the shell owns only this call site, like the appearance control.
  push(mountHelpMenu(root.querySelector('#hcHelp') as HTMLElement));

  // Phone "⋯" affordance (owner ruling 2026-09-06): on a phone the header
  // keeps only the brand + this trigger, and help/appearance/account collapse
  // into the sheet it toggles (`.hc-headtools`). Above 640px the trigger is
  // display:none and the tools sit inline exactly as before, so toggling the
  // class there is a no-op — the desktop header is byte-identical. The two
  // document listeners are torn down with the shell (an unhonoured teardown
  // is one leaked listener per navigation — the same rule the shell enforces
  // for the theme control and account menu).
  {
    const moreBtn = root.querySelector('#hcMore') as HTMLButtonElement | null;
    const headTools = root.querySelector('#hcHeadTools') as HTMLElement | null;
    if (moreBtn && headTools) {
      const closeMore = (): void => {
        headTools.classList.remove('hc-headtools-open');
        moreBtn.setAttribute('aria-expanded', 'false');
      };
      const onDocClick = (ev: MouseEvent): void => {
        if (!headTools.classList.contains('hc-headtools-open')) return;
        const t = ev.target as Node;
        if (headTools.contains(t) || moreBtn.contains(t)) return;
        closeMore();
      };
      const onKey = (ev: KeyboardEvent): void => { if (ev.key === 'Escape') closeMore(); };
      moreBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const open = headTools.classList.toggle('hc-headtools-open');
        moreBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      document.addEventListener('click', onDocClick);
      document.addEventListener('keydown', onKey);
      push(() => {
        document.removeEventListener('click', onDocClick);
        document.removeEventListener('keydown', onKey);
      });
    }
  }

  const whoami = (async () => {
    const { status, data } = await jsonFetch<Me>('/api/me');
    if (status === 401) {
      // One rule for every page: an expired session goes back to login
      // rather than rendering a chrome that can only 401.
      location.href = '/auth/login';
      return { me: null, status };
    }
    const me = status === 200 ? data : null;
    // Reveal, never the reverse: entries a non-admin cannot use stay hidden —
    // a nav entry that leads to "Admin only" is worse than none. The header
    // link and its bottom-tab-bar twin are revealed together, so the phone bar
    // and the desktop nav always show the same destinations.
    const reveal = (...ids: string[]): void => {
      for (const id of ids) (root.querySelector('#' + id) as HTMLElement | null)?.removeAttribute('hidden');
    };
    if (canManage(me)) {
      reveal('hcNavUsers', 'hcTabUsers', 'hcNavAudit', 'hcTabAudit');
    }
    if (canManageTemplates(me)) {
      reveal('hcNavTemplates', 'hcTabTemplates');
    }
    // THE avatar menu (owner ruling 2026-09-08): appearance on top, API
    // tokens, Log out — one shared module on the Bridge, the Helm and both
    // portals (pi-stack avatar-menu.ts via account-menu.ts). API docs stay in
    // the help submenu.
    push(mountAccountMenu(
      root.querySelector('#hcAccount') as HTMLElement,
      me ? ({ ...me, email: me.email ?? '', name: me.name ?? null } as AccountUser) : null,
    ));
    return { me, status };
  })();

  return {
    pagebar: root.querySelector('#hcPagebar') as HTMLElement,
    main: root.querySelector('main.hc-main') as HTMLElement,
    modal: root.querySelector('#hcModal') as HTMLElement,
    whoami,
    teardown() {
      torn = true;
      for (const fn of teardowns.splice(0)) {
        try { fn(); } catch { /* one bad teardown must not strand the rest */ }
      }
    },
  };
}

/**
 * Write the shell's chrome edge into the ground's horizon seat and keep it
 * current; returns the teardown. Everything is optional-chained because the
 * console's node drills mount the shell over a hand-rolled document with no
 * layout — there the seat is simply not written, and nothing throws.
 */
function seatGroundHorizon(pagebar: HTMLElement | null): () => void {
  const html = typeof document !== 'undefined' ? document.documentElement : null;
  if (!pagebar || !html || typeof pagebar.getBoundingClientRect !== 'function' || !html.style?.setProperty) {
    return () => {};
  }
  const write = (): void => {
    const edge = Math.round(pagebar.getBoundingClientRect().bottom);
    if (edge > 0) html.style.setProperty(GROUND_HORIZON_SEAT, `${edge}px`);
  };
  write();
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver === 'function') {
    ro = new ResizeObserver(write);
    ro.observe(pagebar);
  }
  return () => {
    ro?.disconnect();
    html.style.removeProperty(GROUND_HORIZON_SEAT);
  };
}
